import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cls from 'classnames';

import { ToastProps } from './Toast.types';

const getIconColor = (variant: string) => {
	switch (variant) {
		case 'error':
			return 'text-onErrorContainer';
		case 'success':
			return 'text-onSuccessContainer';
		case 'neutral':
		default:
			return 'text-inverseOnSurface';
	}
};
const getVariantClass = (variant: string) => {
	switch (variant) {
		case 'error':
			return 'variant-error';
		case 'success':
			return 'variant-success';
		case 'neutral':
		default:
			return 'variant-neutral';
	}
};

const Toast: FC<ToastProps> = (props) => {
	const { className, Icon, text, variant = 'neutral', onDismiss } = props;
	const [closing, setClosing] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const iconColor = getIconColor(variant);
	const variantClass = getVariantClass(variant);

	const closeToast = useCallback(() => {
		setClosing(true);
		clearTimeout(timeoutRef.current);
		if (onDismiss) onDismiss();
	}, []);

	const handleOnClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		closeToast();
	};

	const duration = useMemo(() => {
		const numberOfCharacters = Array.from(text).length;
		const msPerCharacter = 50;
		const rawDuration = numberOfCharacters * msPerCharacter;
		return Math.min(Math.max(rawDuration, 2000), 7000);
	}, [text]);

	useEffect(() => {
		if (!closing) {
			timeoutRef.current = setTimeout(() => {
				closeToast();
			}, duration);
			return () => clearTimeout(timeoutRef.current);
		}
	}, [closing, closeToast, duration]);

	return (
		<button
			onClick={handleOnClick}
			className={cls('sds-toast', className, variantClass, closing && 'closing')}
		>
			<Icon size="XS" color={iconColor} className="mr-contentXS" />
			<p>{text}</p>
		</button>
	);
};

Toast.displayName = 'Toast';

export default Toast;
